/**
 * Tool type identifiers for studio document tools.
 * These correspond to the tool names registered in the AI SDK.
 */
export const STUDIO_TOOL_TYPES = {
  CREATE_DOCUMENT: 'tool-createDocument',
  TRANSLATE_DOCUMENT: 'tool-translateDocument',
  EDIT_DOCUMENT: 'tool-editDocument',
  CREATE_SUBAGENT: 'tool-createSubAgent',
  CREATE_ARTIFACT: 'tool-createArtifact',
} as const;

/**
 * Array of all studio document tool types for easier iteration.
 */
export const STUDIO_DOCUMENT_TOOL_TYPES = [
  STUDIO_TOOL_TYPES.CREATE_DOCUMENT,
  STUDIO_TOOL_TYPES.TRANSLATE_DOCUMENT,
  STUDIO_TOOL_TYPES.EDIT_DOCUMENT,
  STUDIO_TOOL_TYPES.CREATE_SUBAGENT,
  STUDIO_TOOL_TYPES.CREATE_ARTIFACT,
] as const;

/** Tools whose output is a lifecycle event (`kind`, `status`) rather than the flat `{ documentId, generating, error }`. */
export const LIFECYCLE_STUDIO_TOOL_TYPES = [STUDIO_TOOL_TYPES.CREATE_SUBAGENT, STUDIO_TOOL_TYPES.CREATE_ARTIFACT] as const;

export type StudioToolType = (typeof STUDIO_TOOL_TYPES)[keyof typeof STUDIO_TOOL_TYPES];

/**
 * Why a create saved less than it was asked for. The server yields one of these beside
 * `partial: true` on the `createDocument` output; the client switches its notice copy on it, so
 * both sides read the same constant and a new reason cannot reach the teacher unnamed.
 */
export const STUDIO_PARTIAL_REASONS = {
  /** The generation's own time budget (the route ceiling less a margin) fired first. */
  TIME_BUDGET_EXHAUSTED: 'time_budget_exhausted',
  /** The worker's object never validated or parsed; the last streamed partial was kept. */
  INVALID_OBJECT: 'invalid_object',
  /** The model provider's content filter ended the stream mid-document, and the one retry did not finish it. */
  CONTENT_FILTER: 'content_filter',
  /** The worker ran into its output token cap before the document was finished. */
  OUTPUT_CAP: 'output_cap',
} as const;

export type StudioPartialReason = (typeof STUDIO_PARTIAL_REASONS)[keyof typeof STUDIO_PARTIAL_REASONS];

/**
 * Why a create made no document and will not be retried this turn. The server yields one beside
 * `error` on the `createDocument` output and the client shows the matching notice.
 */
export const STUDIO_CREATE_FAILURE_REASONS = {
  /** The teacher's image allowance is spent for now. */
  USAGE_LIMIT: 'usage_limit',
  /** The safety checks refused the request. */
  BLOCKED: 'blocked',
  /** The teacher may not generate images. */
  NO_ACCESS: 'no_access',
  /** Anything else. */
  FAILED: 'failed',
} as const;

export type StudioCreateFailureReason = (typeof STUDIO_CREATE_FAILURE_REASONS)[keyof typeof STUDIO_CREATE_FAILURE_REASONS];
